import { Children, forwardRef } from 'react'
import styles from './Button.module.scss'

/**
 * Wrapper that groups Button children with shared borders (no gap).
 *
 * Targets `<button>` elements via element selector — works with any className
 * on the child buttons. Right (or bottom) border is removed from all but the
 * last child to prevent double borders.
 *
 * @param {object}          props
 * @param {boolean}         [props.vertical=false]  - stack buttons vertically
 * @param {boolean}         [props.sliding=false]   - animated pill indicator that
 *   slides to the currently selected button. Assumes all child buttons are
 *   equal-width (horizontal) or equal-height (vertical).
 *   When no button is selected the indicator is not rendered.
 * @param {'xsmall'|'small'|'medium'|'large'|'xlarge'} [props.size='large']
 *   geometry scale of the group border / indicator; should match child Button size.
 * @param {string}          [props.className]       - extra class on root element
 * @param {object}          [props.style]           - inline styles on root element
 * @param {string}          [props.role='group']    - semantic role on root element
 * @param {React.ReactNode} props.children          - Button elements
 */
export const ButtonGroup = forwardRef(function ButtonGroup({
  vertical = false,
  sliding = false,
  size = 'large',
  selectedIndex: selectedIndexProp,
  className,
  style,
  role = 'group',
  children,
  ...rest
}, ref) {
  const sizeClass = {
    xsmall: styles.groupSizeXSmall,
    small: styles.groupSizeSmall,
    medium: styles.groupSizeMedium,
    large: styles.groupSizeLarge,
    xlarge: styles.groupSizeXLarge,
  }[size] ?? styles.groupSizeLarge

  const classes = [
    vertical ? styles.groupVertical : styles.group,
    sizeClass,
    className,
  ].filter(Boolean).join(' ')

  // ── Slide indicator ───────────────────────────────────────────────────────
  const childArray = Children.toArray(children)
  // selectedIndexProp lets callers override auto-detection — needed when buttons
  // are wrapped in Tooltip/Fragment and child.props.selected is not directly visible.
  const selectedIndex = selectedIndexProp !== undefined
    ? selectedIndexProp
    : sliding
      ? childArray.findIndex((child) => child.props?.selected)
      : -1
  const count = childArray.length

  // Position uses percentages — valid only when all children are equal-sized.
  const indicatorStyle = selectedIndex >= 0 && count > 0
    ? vertical
      ? { top: `calc(${selectedIndex} / ${count} * 100%)`, height: `calc(100% / ${count})`, left: 0, right: 0 }
      : { left: `calc(${selectedIndex} / ${count} * 100%)`, width: `calc(100% / ${count})`, top: 0, bottom: 0 }
    : null

  return (
    <div
      ref={ref}
      {...rest}
      className={classes}
      style={style}
      role={role}
      data-slide={sliding || undefined}
    >
      {indicatorStyle && (
        <div
          className={vertical ? styles.slideIndicatorV : styles.slideIndicator}
          style={indicatorStyle}
        />
      )}
      {children}
    </div>
  )
})
